// Google Analytics utility functions for ReactEmbed
// These functions will be available globally for embedded embeds

// Generate a unique session ID for tracking user journey
const generateSessionId = () => {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Get or create session ID
const getSessionId = () => {
    let sessionId = sessionStorage.getItem('sierra_embed_session_id');
    if (!sessionId) {
        sessionId = generateSessionId();
        sessionStorage.setItem('sierra_embed_session_id', sessionId);
    }
    return sessionId;
};

// Track funnel steps
let funnelStep = 0;
const getNextFunnelStep = () => {
    funnelStep++;
    return funnelStep;
};

// Event deduplication to prevent multiple identical events
const recentEvents = new Map();
const EVENT_DEDUP_WINDOW = 2000; // 2 seconds

const isDuplicateEvent = (eventName, parameters) => {
    const eventKey = `${eventName}_${JSON.stringify(parameters)}`;
    const now = Date.now();
    const lastEventTime = recentEvents.get(eventKey);
    
    if (lastEventTime && (now - lastEventTime) < EVENT_DEDUP_WINDOW) {
        return true; // Duplicate event within time window
    }
    
    recentEvents.set(eventKey, now);
    
    // Clean up old entries periodically
    if (recentEvents.size > 50) {
        for (const [key, time] of recentEvents.entries()) {
            if (now - time > EVENT_DEDUP_WINDOW * 2) {
                recentEvents.delete(key);
            }
        }
    }
    
    return false;
};

const getTrackingId = () => {
    return window.SIERRA_TRACKING_ID;
};

const getCustomerId = () => {
    return window.SIERRA_CUSTOMER_ID || 'unknown';
};

// Retry mechanism for GA availability
const waitForGA = (callback, maxRetries = 10, delay = 500) => {
    if (typeof window.gtag !== 'undefined') {
        callback();
    } else if (maxRetries > 0) {
        console.log(`Waiting for Google Analytics... (${maxRetries} retries left)`);
        setTimeout(() => waitForGA(callback, maxRetries - 1, delay), delay);
    } else {
        console.error('Google Analytics failed to load after maximum retries');
    }
};

export const trackEvent = (eventName, parameters = {}) => {
    // Check for duplicate events first
    if (isDuplicateEvent(eventName, parameters)) {
        console.log(`Sierra Embed - Duplicate event blocked: ${eventName}`, parameters);
        return;
    }
    
    // Enhanced debugging for staging
    console.log('trackEvent called:', {
        eventName,
        parameters,
        gtagAvailable: typeof window.gtag !== 'undefined',
        windowDataLayer: !!window.dataLayer,
        trackingId: getTrackingId(),
        customerId: getCustomerId()
    });
    
    if (typeof window.gtag !== 'undefined') {
        const sessionId = getSessionId();
        const step = getNextFunnelStep();
        
        window.gtag('event', eventName, {
            ...parameters,
            // Add journey tracking
            session_id: sessionId,
            funnel_step: step,
            timestamp: Date.now(),
            // Add embed context
            custom_parameter_embed_source: 'sierra_parts_embed',
            custom_parameter_customer_id: getCustomerId()
        });
        console.log(`Sierra Embed - GA Event tracked [Step ${step}]:`, eventName, parameters, 'Session:', sessionId, 'Customer:', getCustomerId());
    } else {
        console.warn('Sierra Embed - Google Analytics not available, attempting retry...');
        waitForGA(() => {
            console.log('Retrying trackEvent after GA became available');
            trackEvent(eventName, parameters);
        }, 3, 1000);
    }
};

export const trackPageView = (pageTitle, pagePath) => {
    const trackingId = getTrackingId();
    if (typeof window.gtag !== 'undefined' && trackingId) {
        window.gtag('config', trackingId, {
            page_title: pageTitle,
            page_location: window.location.href,
            page_path: pagePath,
            custom_parameter_embed_source: 'sierra_parts_embed',
            custom_parameter_customer_id: getCustomerId()
        });
        console.log('Sierra Embed - GA Page view tracked:', pageTitle, pagePath, 'Customer:', getCustomerId());
    } else {
        console.warn('Sierra Embed - Google Analytics not available for page view, attempting retry...');
        waitForGA(() => {
            console.log('Retrying trackPageView after GA became available');
            trackPageView(pageTitle, pagePath);
        }, 3, 1000);
    }
};

export const trackEmbedEntry = () => {
    console.log('trackEmbedEntry called - Customer ID:', getCustomerId(), 'Tracking ID:', getTrackingId());
    trackEvent('embed_entry', {
        event_category: 'user_journey',
        event_label: 'step_1_entry',
        funnel_position: 1
    });
};

export const trackSearch = (searchTerm, searchResults = null) => {
    trackEvent('search', {
        search_term: searchTerm,
        ...(searchResults && { search_results: searchResults }),
        event_category: 'user_journey',
        event_label: 'step_2_search',
        funnel_position: 2
    });
};

export const trackSearchResults = (searchTerm, resultCount) => {
    trackEvent('search_results_viewed', {
        search_term: searchTerm,
        result_count: resultCount,
        event_category: 'user_journey',
        event_label: 'step_3_results',
        funnel_position: 3
    });
};

export const trackProductClick = (productId, productName, source = 'search') => {
    trackEvent('product_click', {
        product_id: productId,
        product_name: productName,
        click_source: source,
        event_category: 'user_journey',
        event_label: 'step_4_product_click',
        funnel_position: 4
    });
};

export const trackAddToCart = (itemId, itemName, price, quantity = 1) => {
    const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]+/g, '')) : price;
    
    trackEvent('add_to_cart', {
        currency: 'USD',
        value: numericPrice,
        event_category: 'user_journey',
        event_label: 'step_6_conversion',
        funnel_position: 6,
        items: [{
            item_id: itemId,
            item_name: itemName,
            quantity: quantity,
            price: numericPrice
        }]
    });
};

export const trackViewItem = (itemId, itemName, category, price) => {
    const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]+/g, '')) : price;
    
    trackEvent('view_item', {
        currency: 'USD',
        value: numericPrice,
        event_category: 'user_journey',
        event_label: 'step_4_view_product',
        funnel_position: 4,
        items: [{
            item_id: itemId,
            item_name: itemName,
            item_category: category,
            price: numericPrice
        }]
    });
};

export const trackReadMore = (productId, productName) => {
    trackEvent('product_read_more', {
        product_id: productId,
        product_name: productName,
        event_category: 'user_journey',
        event_label: 'step_5_read_more',
        funnel_position: 5
    });
};

export const trackEmbedInteraction = (action, embedType, details = {}) => {
    trackEvent('embed_interaction', {
        embed_type: embedType,
        action: action,
        event_category: 'embed_interaction',
        ...details
    });
};

// Reset session (call this when embed loads fresh)
export const resetSession = () => {
    sessionStorage.removeItem('sierra_embed_session_id');
    funnelStep = 0;
};

// Make analytics functions available globally for embedded use
if (typeof window !== 'undefined') {
    window.SierraPartsAnalytics = {
        trackEvent,
        trackPageView,
        trackEmbedEntry,
        trackSearch,
        trackSearchResults,
        trackProductClick,
        trackAddToCart,
        trackViewItem,
        trackReadMore,
        trackEmbedInteraction,
        getCustomerId,
        getTrackingId,
        getSessionId,
        resetSession
    };
}
